import { iconKeyboard } from "@kensho/icons";
import { useLogger } from "@kensho/lumberjack";
import { Icon, Tooltip, useToaster } from "@kensho/neo";
import { throttle } from "lodash-es";
import { use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileRejection } from "react-dropzone";

import { ErrorDialog } from "../../components/error-dialog";
import {
  ResizableContainer,
  ResizableContainerRef,
} from "../../components/resizable-container";
import { useKeyboardShortcut } from "../../hooks/use-keyboard-shortcut";
import { useMultiplayerContext } from "../../hooks/use-multiplayer-context";
import { useTranscriptSelection } from "../../hooks/use-transcript-selection";
import { TranscriptionContext } from "../../providers/transcription-context";
import { useTranscriptContext } from "../../providers/use-transcript-context";
import { ShortcutDrawer } from "../../shortcuts/shortcut-drawer";
import { SHORTCUTS } from "../../shortcuts/shortcut-registration";
import {
  APITranscriptToken,
  DispatchEditorActionType,
  EditorAction,
  ScribeError,
  TranscriptionConfiguration,
  TranscriptSelection,
} from "../../types/types";
import { TranscriptEditAction } from "../../utils/transcript-revision-utils";
import { transformSelection } from "../../utils/transform-selection";
import { ActionBar } from "./action-bar";
import { PLAYBACK_RATES } from "./actions/playback-rate-button";
import { FileUpload } from "./file-upload";
import { PlaybackUploadDialog } from "./playback-upload-dialog";
import { TimestampManager } from "./timestamp-manager/timestamp-manager";
import { TranscriptPermissionsContext } from "./transcript-permissions-provider";
import { DispatchEditorActionProvider } from "./transcript/dispatch-editor-action-provider";
import {
  TranscriptContainer,
  TranscriptContainerRef,
} from "./transcript/transcript-container";

interface Props {
  transcriptId: string;
  mediaFile?: File;
  setMediaFile: React.Dispatch<React.SetStateAction<File | undefined>>;
  transcriptionConfiguration: TranscriptionConfiguration;
  setTranscriptionConfiguration: React.Dispatch<
    React.SetStateAction<TranscriptionConfiguration>
  >;
  reset: () => void;
}

export function Transcription({
  transcriptId,
  mediaFile,
  setMediaFile,
  transcriptionConfiguration,
  setTranscriptionConfiguration,
  reset,
}: Props) {
  const { stage } = use(TranscriptionContext);

  const [isTranscriptFocused, setIsTranscriptFocused] = useState(false);
  const { transcriptSelection, updateTranscriptSelection } =
    useTranscriptSelection(isTranscriptFocused);

  const {
    transcript,
    version,
    dispatch: transcriptContextDispatch,
    undos,
    redos,
  } = useTranscriptContext({
    onRevision: ({ revision, revisionMeta }) => {
      if (revisionMeta.fromServer && isTranscriptFocused) {
        const nextSelection = transformSelection(transcriptSelection, revision);
        updateTranscriptSelection(nextSelection, false);
      }
    },
  });

  const { transcriptPermissions } = use(TranscriptPermissionsContext);
  const toaster = useToaster();

  const [isShortcutOpen, setIsShortcutOpen] = useState(false);

  const [error, setError] = useState<ScribeError>();
  const [currentTime, setCurrentTime] = useState(0); // seconds
  const [duration, setDuration] = useState<number>(); // seconds
  const [showTimestampManager, setShowTimestampManager] = useState(false);

  const { sendMessage, sessionId } = useMultiplayerContext((message): void => {
    if (
      !(
        message.type === "error" &&
        (message.payload.code === "S2T-EDT-002" ||
          message.payload.code === "S2T-EDT-003")
      )
    )
      return;

    setError({
      type:
        message.payload.code === "S2T-EDT-002"
          ? "saveTranscriptError"
          : "saveTranscriptConflict",
      title: message.payload.title,
      detail: message.payload.detail,
    });
  });

  // media
  const [mediaPlayable, setMediaPlayable] = useState(true);
  const [paused, setPaused] = useState(true);
  const [playbackRate, setPlaybackRate] = useState(1);

  // transcript
  const transcriptContainerRef = useRef<TranscriptContainerRef>(null);

  const handleFileSwitch = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]): void => {
      if (acceptedFiles.length + fileRejections.length > 1) {
        toaster.show({
          label: `Only one audio/video file can be uploaded at a time`,
          intent: "danger",
        });
        return;
      }

      if (fileRejections.length) {
        toaster.show({
          label: `Unsupported file. Please upload a valid audio/video file`,
          intent: "danger",
        });
        return;
      }

      setMediaPlayable(true);
      setMediaFile(acceptedFiles[0]);
    },
    [toaster, setMediaFile],
  );

  const [mediaEle, setMediaEle] = useState<HTMLAudioElement | null>(null);

  const onMediaError = useCallback((e?: ScribeError) => {
    // if the file loads but is not playable, skip playback but still let them transcribe
    if (!e) {
      setMediaPlayable(false);
      return;
    }

    setError(e);
  }, []);

  const seekMedia = useCallback(
    ({
      timeSeconds,
      play = true,
      scroll = true,
    }: {
      timeSeconds: number;
      play?: boolean;
      scroll?: boolean;
    }): void => {
      if (mediaFile && mediaPlayable && mediaEle) {
        if (timeSeconds >= 0 && timeSeconds <= mediaEle.duration) {
          // eslint-disable-next-line react-hooks/immutability
          mediaEle.currentTime = timeSeconds;
          if (play) {
            setPaused(false);
          }
          setCurrentTime(timeSeconds);
        } else if (timeSeconds <= 0) {
          mediaEle.currentTime = 0;
          setCurrentTime(0);
        } else {
          mediaEle.currentTime = mediaEle.duration;
          setCurrentTime(mediaEle.duration);
        }
      }
      if (scroll)
        transcriptContainerRef.current?.scrollTranscriptToTime(
          timeSeconds * 1000,
        );
    },
    [mediaEle, mediaFile, mediaPlayable],
  );

  const onClickToken = useCallback(
    (token: APITranscriptToken): void => {
      if (stage !== "POST_TRANSCRIPTION") return;
      if (paused)
        seekMedia({
          timeSeconds: token.startMs / 1000,
          play: false,
          scroll: false,
        });
    },
    [seekMedia, stage, paused],
  );

  const dispatchEditorAction = useCallback<DispatchEditorActionType>(
    (editorAction: EditorAction, baseVersion: number): void => {
      if (!transcriptPermissions.edit) return;
      // update client state
      transcriptContextDispatch({
        type: "clientEditorAction",
        editorAction,
        baseVersion,
      });
      if (!editorAction.selectionChangeDisabled)
        updateTranscriptSelection(editorAction.afterSelection);
    },
    [
      transcriptPermissions,
      transcriptContextDispatch,
      updateTranscriptSelection,
    ],
  );

  const log = useLogger();
  const onEditOperationError = useCallback(
    (editOperationError: Error, action: TranscriptEditAction): void => {
      log.error(editOperationError, { actionType: action.type, transcriptId });
    },
    [transcriptId, log],
  );

  function undo(): void {
    if (!transcriptPermissions.edit) return;
    const last = undos.at(-1);
    if (!last) return;
    transcriptContextDispatch({ type: "undo" });

    if (!last.selectionChangeDisabled) {
      setIsTranscriptFocused(true);
      updateTranscriptSelection(last.beforeSelection, false);
    }
  }
  function redo(): void {
    if (!transcriptPermissions.edit) return;
    const last = redos.at(-1);
    if (!last) return;
    transcriptContextDispatch({ type: "redo" });

    if (!last.selectionChangeDisabled) {
      setIsTranscriptFocused(true);
      updateTranscriptSelection(last.afterSelection, false);
    }
  }

  useKeyboardShortcut(
    SHORTCUTS.Audio.skipBackwards.keys,
    () => {
      seekMedia({ timeSeconds: currentTime - 3 });
    },
    {
      enabled: Boolean(mediaFile && mediaPlayable),
      preventDefault: true,
    },
  );
  useKeyboardShortcut(
    SHORTCUTS.Audio.playPause.keys,
    () => {
      setPaused(!paused);
    },
    {
      enabled: Boolean(mediaFile && mediaPlayable),
    },
  );

  useKeyboardShortcut(
    SHORTCUTS.Audio.increaseSpeed.keys,
    () => {
      setPlaybackRate(
        PLAYBACK_RATES[
          Math.min(
            PLAYBACK_RATES.length - 1,
            PLAYBACK_RATES.indexOf(playbackRate) + 1,
          )
        ],
      );
    },
    {
      enabled: Boolean(mediaFile && mediaPlayable),
    },
  );
  useKeyboardShortcut(
    SHORTCUTS.Audio.decreaseSpeed.keys,
    () => {
      setPlaybackRate(
        PLAYBACK_RATES[Math.max(0, PLAYBACK_RATES.indexOf(playbackRate) - 1)],
      );
    },
    {
      enabled: Boolean(mediaFile && mediaPlayable),
    },
  );
  useKeyboardShortcut(
    SHORTCUTS.Audio.skipForwards.keys,
    () => {
      seekMedia({ timeSeconds: currentTime + 3 });
    },
    {
      enabled: Boolean(mediaFile && mediaPlayable),
      preventDefault: true,
    },
  );

  useKeyboardShortcut(
    SHORTCUTS.View.toggleShortcutDrawer.keys,
    () => setIsShortcutOpen((prev) => !prev),
    {
      preventDefault: false,
      ignoreModifiers: true,
      enableOnContentEditable: false,
    },
  );

  const bottomBarContainerRef = useRef<ResizableContainerRef>(null);

  useKeyboardShortcut(
    SHORTCUTS.View.toggleTimeline.keys,
    () => {
      const nextShowTimestampManager = !showTimestampManager;
      setShowTimestampManager(nextShowTimestampManager);

      if (nextShowTimestampManager) {
        bottomBarContainerRef.current?.setSize({ height: 224 });
      } else {
        bottomBarContainerRef.current?.setSize({ height: 80 });
      }
    },
    {
      preventDefault: false,
      enableOnContentEditable: false,
      enabled: stage === "POST_TRANSCRIPTION",
    },
  );

  const throttledSendCursorUpdate = useMemo(
    () =>
      throttle((selection: TranscriptSelection | null) => {
        if (selection && sessionId)
          sendMessage({
            type: "cursor-update",
            payload: { start: selection.start, end: selection.end, sessionId },
          });
      }, 500),
    [sendMessage, sessionId],
  );

  useEffect(() => {
    throttledSendCursorUpdate(transcriptSelection);

    // periodically send cursor updates even when transcriptSelecition is unchanged
    const interval = window.setInterval(() => {
      throttledSendCursorUpdate(transcriptSelection);
    }, 3000);

    return () => {
      clearInterval(interval);
      throttledSendCursorUpdate.cancel();
    };
  }, [transcriptSelection, throttledSendCursorUpdate]);

  return (
    <DispatchEditorActionProvider dispatchEditorAction={dispatchEditorAction}>
      <div className="flex h-[calc(100vh-80px)] flex-col overflow-hidden">
        <ErrorDialog
          isOpen={!!error}
          error={error}
          onClose={() => setError(undefined)}
        />

        <div className="m-auto mt-3 flex w-full max-w-[100vw] flex-auto justify-center gap-10 overflow-hidden">
          <TranscriptContainer
            ref={transcriptContainerRef}
            showPlayControls={Boolean(mediaFile)}
            stage={stage}
            currentTimeMs={currentTime * 1000}
            paused={paused}
            onClickToken={onClickToken}
            seekMedia={seekMedia}
            transcriptionConfiguration={transcriptionConfiguration}
            setTranscriptionConfiguration={setTranscriptionConfiguration}
            transcriptSelection={transcriptSelection}
            updateTranscriptSelection={updateTranscriptSelection}
            onEditOperationError={onEditOperationError}
            undo={undo}
            redo={redo}
            isTranscriptFocused={isTranscriptFocused}
            setIsTranscriptFocused={setIsTranscriptFocused}
            setPaused={setPaused}
            media={mediaFile}
            transcriptId={transcriptId}
            reset={reset}
          />
        </div>

        <ResizableContainer
          ref={bottomBarContainerRef}
          className="max-h-56 flex-none border-t border-gray-200"
          disabled={!transcript || stage !== "POST_TRANSCRIPTION"}
          initialWidth="100vw"
          initialHeight={80}
          minHeight={80}
          resize={{ north: true }}
          onResize={({ height }) =>
            setShowTimestampManager(typeof height === "number" && height > 80)
          }
        >
          {["POST_TRANSCRIPTION"].includes(stage) && (
            <>
              <ShortcutDrawer
                isOpen={isShortcutOpen}
                onClose={() => setIsShortcutOpen(false)}
              />
              <Tooltip content="Keyboard shortcuts">
                <button
                  data-testid="keyboard-shortcuts-button"
                  className="absolute -top-14 right-5 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/10 hover:bg-gray-50 active:bg-gray-100"
                  type="button"
                  onClick={() => setIsShortcutOpen((prev) => !prev)}
                >
                  <Icon icon={iconKeyboard} />
                </button>
              </Tooltip>
            </>
          )}

          <FileUpload
            handleFileDrop={(acceptedFiles, fileRejections) =>
              handleFileSwitch(acceptedFiles, fileRejections)
            }
            enableDragState={false}
          >
            {({ open }) => (
              <>
                <PlaybackUploadDialog
                  stage={stage}
                  transcriptId={transcriptId}
                  hasMedia={Boolean(mediaFile)}
                  openFileChooser={open}
                />
                <ActionBar
                  onMediaError={onMediaError}
                  setMediaEle={setMediaEle}
                  transcript={transcript}
                  mediaFile={mediaFile}
                  mediaPlayable={mediaPlayable}
                  currentTime={currentTime}
                  seekMedia={seekMedia}
                  paused={paused}
                  setPaused={setPaused}
                  duration={duration}
                  playbackRate={playbackRate}
                  setPlaybackRate={setPlaybackRate}
                  setCurrentTime={setCurrentTime}
                  setDuration={setDuration}
                  openFileChooser={open}
                />
              </>
            )}
          </FileUpload>

          {["POST_TRANSCRIPTION"].includes(stage) &&
            showTimestampManager &&
            transcript && (
              <TimestampManager
                transcript={transcript}
                version={version}
                currentTimeMs={currentTime * 1000}
                mediaEle={mediaEle}
                paused={paused}
                seekMedia={seekMedia}
              />
            )}
        </ResizableContainer>
      </div>
    </DispatchEditorActionProvider>
  );
}
